<div class="ai-segment-suggestions">
    <!-- Header -->
    <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex align-items-center gap-2">
            <i class="fa-solid fa-wand-magic-sparkles text-primary text-xl"></i>
            <span class="text-lg font-semibold">KI-Segmentvorschläge</span>
        </div>
        <p-button
            label="Vorschläge generieren"
            icon="fa-solid fa-sparkles"
            styleClass="p-button-outlined"
            (onClick)="openGenerateDialog()">
        </p-button>
    </div>

    <!-- Error Message -->
    <p-message *ngIf="errorMessage" severity="error" [text]="errorMessage" styleClass="mb-3 w-full"></p-message>

    <!-- Loading State -->
    <div *ngIf="loading" class="flex align-items-center justify-content-center p-4">
        <i class="fa-solid fa-spinner fa-spin text-2xl text-primary"></i>
    </div>

    <!-- Suggestions List -->
    <div *ngIf="!loading && suggestions.length > 0" class="grid">
        <div *ngFor="let suggestion of suggestions" class="col-12 md:col-6 lg:col-4">
            <div class="p-card h-full">
                <div class="p-card-body">
                    <!-- Suggestion Header -->
                    <div class="flex align-items-start justify-content-between mb-3">
                        <div>
                            <h3 class="text-lg font-semibold m-0 mb-1">{{ suggestion.title }}</h3>
                            <span class="text-sm border-round px-2 py-1" [ngClass]="getStatusColor(suggestion.status)">
                                {{ suggestion.status }}
                            </span>
                        </div>
                        <div class="flex align-items-center gap-1">
                            <span class="text-sm font-medium" [ngClass]="getConfidenceColor(suggestion.confidenceScore)">
                                {{ (suggestion.confidenceScore * 100) | number:'1.0-0' }}%
                            </span>
                            <span class="text-xs text-500">Konfidenz</span>
                        </div>
                    </div>

                    <!-- Description -->
                    <p class="text-sm text-600 mb-3">{{ suggestion.description }}</p>

                    <!-- Metrics -->
                    <div class="grid mb-3">
                        <div class="col-6">
                            <div class="text-xs text-500">Gesch. Mitglieder</div>
                            <div class="text-lg font-semibold">{{ suggestion.estimatedMemberCount | number }}</div>
                        </div>
                        <div class="col-6" *ngIf="suggestion.estimatedConversionRate">
                            <div class="text-xs text-500">Gesch. Konversion</div>
                            <div class="text-lg font-semibold">{{ (suggestion.estimatedConversionRate * 100) | number:'1.1-1' }}%</div>
                        </div>
                    </div>

                    <!-- Rationale -->
                    <div *ngIf="suggestion.rationale" class="mb-3 p-2 bg-gray-100 border-round">
                        <div class="text-xs text-500 mb-1">KI-Begründung</div>
                        <div class="text-sm">{{ suggestion.rationale }}</div>
                    </div>

                    <!-- Actions -->
                    <div class="flex align-items-center gap-2 pt-2 border-top-1 surface-border">
                        <p-button
                            label="Übernehmen"
                            icon="fa-solid fa-check"
                            styleClass="p-button-sm p-button-success"
                            (onClick)="applySuggestion(suggestion)">
                        </p-button>
                        <p-button
                            label="Ablehnen"
                            icon="fa-solid fa-times"
                            styleClass="p-button-sm p-button-danger p-button-outlined"
                            (onClick)="rejectSuggestion(suggestion)">
                        </p-button>
                        <p-button
                            icon="fa-solid fa-comment"
                            styleClass="p-button-sm p-button-text"
                            pTooltip="Feedback geben"
                            (onClick)="openFeedbackDialog(suggestion)">
                        </p-button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- No Suggestions State -->
    <div *ngIf="!loading && suggestions.length === 0 && existingSuggestions.length === 0"
         class="flex flex-column align-items-center justify-content-center p-5 text-center">
        <i class="fa-solid fa-lightbulb text-4xl text-300 mb-3"></i>
        <p class="text-lg text-600 mb-2">Noch keine KI-Vorschläge</p>
        <p class="text-sm text-500 mb-3">Generieren Sie Vorschläge basierend auf Ihren Kampagnenzielen</p>
        <p-button
            label="Vorschläge generieren"
            icon="fa-solid fa-sparkles"
            (onClick)="openGenerateDialog()">
        </p-button>
    </div>

    <!-- Existing Suggestions Section -->
    <div *ngIf="existingSuggestions.length > 0" class="mt-4">
        <h4 class="text-md font-semibold mb-3">Vorherige Vorschläge</h4>
        <p-table [value]="existingSuggestions" [paginator]="true" [rows]="5" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th>Titel</th>
                    <th>Konfidenz</th>
                    <th>Status</th>
                    <th>Generiert</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-suggestion>
                <tr>
                    <td>{{ suggestion.suggestionTitle }}</td>
                    <td>
                        <span [ngClass]="getConfidenceColor(suggestion.confidenceScore)">
                            {{ (suggestion.confidenceScore * 100) | number:'1.0-0' }}%
                        </span>
                    </td>
                    <td>
                        <span class="text-sm border-round px-2 py-1" [ngClass]="getStatusColor(suggestion.suggestionStatus)">
                            {{ suggestion.suggestionStatus }}
                        </span>
                    </td>
                    <td>{{ suggestion.generatedAt | date:'short' }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<!-- Generate Dialog -->
<p-dialog
    header="KI-Vorschläge generieren"
    [(visible)]="showGenerateDialog"
    [modal]="true"
    [style]="{width: '500px'}"
    [closable]="!generating">

    <div class="flex flex-column gap-3">
        <div>
            <label for="campaignGoal" class="block text-sm font-medium mb-2">Kampagnenziel</label>
            <textarea
                id="campaignGoal"
                pInputTextarea
                [(ngModel)]="campaignGoal"
                rows="4"
                class="w-full"
                placeholder="Beschreiben Sie Ihr Kampagnenziel, z.B. 'Bindung von hochwertigen Kunden mit Abwanderungsrisiko erhöhen'">
            </textarea>
        </div>
        <p class="text-sm text-500">
            Die KI analysiert Ihr Ziel und generiert Segmentkriterien-Vorschläge, die für Ihr Ziel optimiert sind.
        </p>
    </div>

    <ng-template pTemplate="footer">
        <p-button
            label="Abbrechen"
            styleClass="p-button-text"
            (onClick)="showGenerateDialog = false"
            [disabled]="generating">
        </p-button>
        <p-button
            label="Generieren"
            icon="fa-solid fa-sparkles"
            (onClick)="generateSuggestions()"
            [loading]="generating">
        </p-button>
    </ng-template>
</p-dialog>

<!-- Feedback Dialog -->
<p-dialog
    header="Feedback geben"
    [(visible)]="showFeedbackDialog"
    [modal]="true"
    [style]="{width: '400px'}">

    <div class="flex flex-column gap-3" *ngIf="selectedSuggestion">
        <div>
            <label class="block text-sm font-medium mb-2">Bewertung</label>
            <p-rating [(ngModel)]="feedbackRating"></p-rating>
        </div>
        <div>
            <label for="feedbackComments" class="block text-sm font-medium mb-2">Kommentare (optional)</label>
            <textarea
                id="feedbackComments"
                pInputTextarea
                [(ngModel)]="feedbackComments"
                rows="3"
                class="w-full"
                placeholder="Teilen Sie Ihre Meinung zu diesem Vorschlag...">
            </textarea>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <p-button
            label="Abbrechen"
            styleClass="p-button-text"
            (onClick)="showFeedbackDialog = false">
        </p-button>
        <p-button
            label="Feedback senden"
            icon="fa-solid fa-paper-plane"
            (onClick)="submitFeedback()">
        </p-button>
    </ng-template>
</p-dialog>
